import { theme as antdTheme, type ThemeConfig } from 'antd'

export const THEME_PREFERENCES = ['light', 'dark', 'system'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]
export type ResolvedTheme = 'light' | 'dark'
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

/**
 * Page background painted by the app shell (`.baseLayout`). antd's
 * `colorBgLayout` is transparent so `Layout` shows the shell through.
 */
const LAYOUT_BACKGROUND: Record<ResolvedTheme, string> = {
  light: 'hsl(200, 21%, 98%)',
  dark: '#0a0b0d',
}

/** Soft drop shadow under borderless cards and the top menu. */
const CARD_SHADOW: Record<ResolvedTheme, string> = {
  light: 'rgba(0, 0, 0, 0.05) 0px 10px 30px -12px',
  dark: 'rgba(0, 0, 0, 0.45) 0px 10px 30px -12px',
}

const sharedTokens: ThemeConfig['token'] = {
  colorBgLayout: 'transparent',
  colorPrimary: '#1890ff',
  fontFamily: '"Satoshi", sans-serif',
  borderRadius: 8,
  borderRadiusSM: 8,
  borderRadiusLG: 12,
}

const sharedComponents: ThemeConfig['components'] = {
  Modal: {
    paddingContentHorizontalLG: antdTheme.getDesignToken().paddingXL,
  },
  Checkbox: {
    borderRadiusSM: 4,
  },
  Button: {
    boxShadow: 'none',
  },
  Badge: {
    textFontSizeSM: 11,
    textFontWeight: 500,
    indicatorHeightSM: 16,
  },
}

// antd applies `token` overrides after the algorithm, so anything that is a
// light-only colour has to live here and not in the shared set, or it would
// leak into dark mode unchanged.
const lightTokens: ThemeConfig['token'] = {
  colorBorderSecondary: '#f7f7f7',
  colorBorder: '#e4e4e4',
}

const lightComponents: ThemeConfig['components'] = {
  Table: {
    headerBg: '#f9fafb',
    headerSplitColor: '#f9fafb',
    footerBg: '#ffffff',
    rowHoverBg: '#fafafa',
  },
}

/** The root antd theme for a resolved colour scheme. */
export function buildThemeConfig(mode: ResolvedTheme): ThemeConfig {
  if (mode === 'dark') {
    return {
      algorithm: antdTheme.darkAlgorithm,
      token: sharedTokens,
      components: sharedComponents,
    }
  }
  return {
    algorithm: antdTheme.defaultAlgorithm,
    token: { ...sharedTokens, ...lightTokens },
    components: { ...sharedComponents, ...lightComponents },
  }
}

/**
 * CSS custom properties for stylesheets (LESS cannot read antd tokens).
 * Projected from the same token set as the antd theme, so both stay in sync.
 * Written to `<html>` by the theme controller.
 */
export function getAppCssVars(mode: ResolvedTheme): Record<string, string> {
  const token = antdTheme.getDesignToken(buildThemeConfig(mode))
  return {
    '--app-bg-layout': LAYOUT_BACKGROUND[mode],
    '--app-bg-container': token.colorBgContainer,
    '--app-bg-elevated': token.colorBgElevated,
    '--app-fill-alter': token.colorFillAlter,
    '--app-fill': token.colorFillTertiary,
    '--app-fill-secondary': token.colorFillSecondary,
    '--app-border': token.colorBorder,
    '--app-border-secondary': token.colorBorderSecondary,
    '--app-text': token.colorText,
    '--app-text-secondary': token.colorTextSecondary,
    '--app-text-tertiary': token.colorTextTertiary,
    '--app-text-disabled': token.colorTextQuaternary,
    '--app-text-on-solid': token.colorTextLightSolid,
    '--app-primary': token.colorPrimary,
    '--app-primary-hover': token.colorPrimaryHover,
    '--app-primary-bg': token.colorPrimaryBg,
    '--app-primary-bg-hover': token.colorPrimaryBgHover,
    '--app-primary-border': token.colorPrimaryBorder,
    '--app-error': token.colorError,
    '--app-error-bg': token.colorErrorBg,
    '--app-error-border': token.colorErrorBorder,
    '--app-success': token.colorSuccess,
    '--app-success-bg': token.colorSuccessBg,
    '--app-warning': token.colorWarning,
    '--app-warning-bg': token.colorWarningBg,
    '--app-warning-border': token.colorWarningBorder,
    '--app-shadow': token.boxShadowTertiary,
    '--app-shadow-card': CARD_SHADOW[mode],
  }
}
